import { supabase } from '@/integrations/supabase/client';

/**
 * Calcola e accredita i punti fedeltà per un ordine
 * Regola: 1 punto ogni €10 spesi
 */
export async function awardLoyaltyPoints(
  userId: string,
  orderId: string,
  orderTotal: number,
  description?: string
) {
  const pointsToAward = Math.floor(orderTotal / 10); // 1 punto ogni €10
  
  if (pointsToAward <= 0) return { success: true, points: 0 };
  
  try {
    // Get or create loyalty record
    const { data: existing } = await supabase
      .from('loyalty_points')
      .select('id, points, lifetime_points')
      .eq('user_id', userId)
      .maybeSingle();
    
    if (existing) {
      // Update existing
      await supabase
        .from('loyalty_points')
        .update({
          points: existing.points + pointsToAward,
          lifetime_points: existing.lifetime_points + pointsToAward
        })
        .eq('id', existing.id);
    } else {
      // Create new
      await supabase
        .from('loyalty_points')
        .insert({
          user_id: userId,
          points: pointsToAward,
          lifetime_points: pointsToAward
        });
    }
    
    // Log transaction
    await supabase
      .from('loyalty_transactions')
      .insert({
        user_id: userId,
        points: pointsToAward,
        transaction_type: 'earn',
        description: description || `Punti per ordine (€${orderTotal.toFixed(2)})`,
        order_id: orderId
      });
    
    return { success: true, points: pointsToAward };
  } catch (error) {
    console.error('Error awarding loyalty points:', error);
    return { success: false, points: 0 };
  }
}

/**
 * Riscatta punti per ottenere uno sconto
 * Regola: 100 punti = €10 di sconto
 */
export async function redeemLoyaltyPoints(
  userId: string,
  pointsToRedeem: number
) {
  if (pointsToRedeem < 100) {
    return { success: false, error: 'Minimo 100 punti richiesti', discount: 0 };
  }
  
  try {
    const { data: current } = await supabase
      .from('loyalty_points')
      .select('id, points')
      .eq('user_id', userId)
      .single();
    
    if (!current || current.points < pointsToRedeem) {
      return { success: false, error: 'Punti insufficienti', discount: 0 };
    }
    
    const discountAmount = Math.floor(pointsToRedeem / 100) * 10;
    const actualPointsUsed = Math.floor(pointsToRedeem / 100) * 100;
    
    // Deduct points
    await supabase
      .from('loyalty_points')
      .update({ points: current.points - actualPointsUsed })
      .eq('id', current.id);
    
    // Log transaction
    await supabase
      .from('loyalty_transactions')
      .insert({
        user_id: userId,
        points: -actualPointsUsed,
        transaction_type: 'redeem',
        description: `Sconto di €${discountAmount} riscattato`
      });
    
    return { success: true, discount: discountAmount, pointsUsed: actualPointsUsed };
  } catch (error) {
    console.error('Error redeeming points:', error);
    return { success: false, error: 'Errore durante il riscatto', discount: 0 };
  }
}

/**
 * Ottiene i punti correnti dell'utente
 */
export async function getUserPoints(userId: string) {
  try {
    const { data } = await supabase
      .from('loyalty_points')
      .select('points, lifetime_points')
      .eq('user_id', userId)
      .maybeSingle();
    
    return data || { points: 0, lifetime_points: 0 };
  } catch {
    return { points: 0, lifetime_points: 0 };
  }
}
